import { z } from "zod";
import { ResourceKind } from "@gen/tracker/v1/common_pb";
import {
  TagManagerSnippetKind,
  TagManagerPlacement,
  TagManagerDestination,
} from "@gen/tracker/v1/tag_manager_pb";

const VALID_SCOPE_KINDS = [
  ResourceKind.WORKSPACE,
  ResourceKind.TRAFFIC_SOURCE,
  ResourceKind.OFFER,
  ResourceKind.CAMPAIGN,
  ResourceKind.LANDER,
] as const;

const VALID_KINDS = [
  TagManagerSnippetKind.GTM,
  TagManagerSnippetKind.PIXEL,
  TagManagerSnippetKind.POSTBACK,
  TagManagerSnippetKind.CUSTOM_JS,
  TagManagerSnippetKind.CUSTOM_HTML,
] as const;

const VALID_PLACEMENTS = [
  TagManagerPlacement.HEAD,
  TagManagerPlacement.BODY_TOP,
  TagManagerPlacement.BODY_BOTTOM,
  TagManagerPlacement.BEFORE_REDIRECT,
] as const;

export const SNIPPET_NAME_MAX_LEN = 120;
export const SNIPPET_CODE_MAX_BYTES = 1 << 20; // matches BE

// Schema mirrors the Go validator in internal/service/tag_manager_svc.go. Any
// change here MUST be reflected there (the BE remains authoritative; this
// schema exists to catch errors before the round-trip).
export const tagManagerSnippetSchema = z
  .object({
    id: z.string().optional(),
    name: z
      .string()
      .trim()
      .min(1, "Укажите название")
      .max(SNIPPET_NAME_MAX_LEN, `Name max ${SNIPPET_NAME_MAX_LEN} chars`),
    kind: z.number().refine((v) => VALID_KINDS.includes(v as (typeof VALID_KINDS)[number]), {
      message: "Выберите тип сниппета",
    }),
    scope: z.object({
      kind: z
        .number()
        .refine((v) => VALID_SCOPE_KINDS.includes(v as (typeof VALID_SCOPE_KINDS)[number]), {
          message: "Выберите область",
        }),
      resourceId: z.string().optional().default(""),
    }),
    placement: z
      .number()
      .refine((v) => VALID_PLACEMENTS.includes(v as (typeof VALID_PLACEMENTS)[number]), {
        message: "Выберите место вставки",
      }),
    code: z
      .string()
      .max(SNIPPET_CODE_MAX_BYTES, "Код больше 1 МиБ"),
    enabled: z.boolean(),
    conditionsExpression: z.string().optional().default(""),
    // M3 — Consent Mode v2 categories that must all be granted before the
    // engine fires this snippet. Empty disables the gate.
    consentRequired: z.array(z.string()).optional().default([]),
    // M4 — destination + key-value config + JSON field mappings. The latter
    // is stored as a string so authors can hand-craft it before we ship a
    // proper key-value editor; we parse + validate on save.
    destination: z
      .number()
      .optional()
      .default(TagManagerDestination.CUSTOM_HTTP),
    destinationConfig: z.record(z.string(), z.string()).optional().default({}),
    fieldMappingsJson: z.string().optional().default("[]"),
  })
  .superRefine((s, ctx) => {
    if (s.scope.kind === ResourceKind.WORKSPACE) {
      if (s.scope.resourceId && s.scope.resourceId !== "") {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "WORKSPACE scope must not carry a resource_id",
          path: ["scope", "resourceId"],
        });
      }
    } else {
      if (!s.scope.resourceId || s.scope.resourceId === "") {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Выберите объект для этой области",
          path: ["scope", "resourceId"],
        });
      }
    }
    if (
      s.placement === TagManagerPlacement.BEFORE_REDIRECT &&
      s.kind === TagManagerSnippetKind.GTM
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "GTM cannot fire BEFORE_REDIRECT — pick HEAD or BODY_BOTTOM",
        path: ["placement"],
      });
    }
    // Field mappings JSON must parse + be an array.
    if (s.fieldMappingsJson && s.fieldMappingsJson.trim() !== "") {
      try {
        const parsed = JSON.parse(s.fieldMappingsJson);
        if (!Array.isArray(parsed)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "field_mappings must be a JSON array",
            path: ["fieldMappingsJson"],
          });
        }
      } catch {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "field_mappings must be valid JSON",
          path: ["fieldMappingsJson"],
        });
      }
    }
  });

export type TagManagerSnippetFormValues = z.infer<typeof tagManagerSnippetSchema>;
